import { apiConfig } from '../utils/apiConfig';
import { ThemeSettings, normalizeThemeSettings } from '../theme/themeSettings';

type ThemeSettingsResponse = {
  success?: boolean;
  settings?: ThemeSettings;
  error?: string;
};

const STORAGE_KEY = 'theme_settings_cache_v1';
let memoryCache: ThemeSettings | null = null;

const readCache = (): ThemeSettings | null => {
  if (memoryCache) return memoryCache;
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    memoryCache = normalizeThemeSettings(parsed);
    return memoryCache;
  } catch {
    return null;
  }
};

const writeCache = (settings: ThemeSettings): void => {
  memoryCache = settings;
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore cache errors
  }
};

const clearCache = (): void => {
  memoryCache = null;
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore cache errors
  }
};

export const themeSettingsService = {
  clearCache,
  async getThemeSettings(token: string, options?: { force?: boolean }): Promise<ThemeSettings> {
    if (!options?.force) {
      const cached = readCache();
      if (cached) return cached;
    }

    const res = await fetch(`${apiConfig.apiUrl}?action=getThemeSettings`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status} while loading theme settings`);
    }
    const data = (await res.json()) as ThemeSettingsResponse;
    if (!data?.success || !data.settings) {
      throw new Error(data?.error || 'Theme settings could not be loaded.');
    }
    const normalized = normalizeThemeSettings(data.settings);
    writeCache(normalized);
    return normalized;
  },
  async updateThemeSettings(token: string, settings: ThemeSettings): Promise<ThemeSettings> {
    const res = await fetch(apiConfig.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        action: 'updateThemeSettings',
        settings
      })
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status} while saving theme settings`);
    }
    const data = (await res.json()) as ThemeSettingsResponse;
    if (!data?.success || !data.settings) {
      throw new Error(data?.error || 'Theme settings could not be saved.');
    }
    const normalized = normalizeThemeSettings(data.settings);
    writeCache(normalized);
    return normalized;
  }
};
